import { useState } from 'react';
import { BLANK_SHIRT_PATH } from '@lib/utils';
import type { Player } from '@api-service/players';

type Props = {
  player: Pick<
    Player,
    'imageUrl' | 'kitImageUrl' | 'displayName' | 'position'
  > & {
    team: Pick<Player['team'], 'shortName'>;
  };
  size?: number;
};

const PlayerFaceWithShirtFallback = ({ player, size }: Props) => {
  const [hasFaceFailed, setHasFaceFailed] = useState(false);
  // Failures are remembered per source URL so a failure only suppresses the
  // source that failed; a kitImageUrl that arrives or changes later is still
  // attempted even after the bundled shirt has failed.
  const [failedShirtSources, setFailedShirtSources] = useState<string[]>([]);
  const sizingStyle = size ? { height: size, width: size } : undefined;
  const sizingClass = size ? '' : 'h-full w-full';

  if (hasFaceFailed || !player.imageUrl) {
    // The backend already picks the goalkeeper variant, so its URL is used
    // as-is. The blank shirt covers a response with no kitImageUrl and a kit
    // that fails to load — there is no per-team bundled artwork to fall back
    // to any more, and none that could be kept current.
    const preferredShirtSource = player.kitImageUrl || BLANK_SHIRT_PATH;
    const shirtSource = failedShirtSources.includes(preferredShirtSource)
      ? BLANK_SHIRT_PATH
      : preferredShirtSource;

    const markShirtSourceFailed = () =>
      setFailedShirtSources((previous) =>
        previous.includes(shirtSource) ? previous : [...previous, shirtSource]
      );

    return (
      <img
        src={shirtSource}
        alt={`${player.team.shortName} shirt`}
        onError={markShirtSourceFailed}
        style={sizingStyle}
        className={`object-contain ${sizingClass}`}
        data-cy="player-shirt"
        data-cy-team-code={player.team.shortName}
      />
    );
  }

  return (
    <img
      src={player.imageUrl}
      alt={player.displayName}
      onError={() => setHasFaceFailed(true)}
      style={sizingStyle}
      className={`rounded-full object-cover bg-neutral-100 ${sizingClass}`}
      data-cy="player-face"
    />
  );
};

export default PlayerFaceWithShirtFallback;
